// Paper themes for letters. Each is a soft pastel "stationery" with a
// paper color, an ink color, and a ruled-line color. A letter is a piece
// of paper, so these light papers are used regardless of the app's
// light/dark mode — a letter shouldn't suddenly turn black.
//
// Free set is the six below. The model leaves room for paid users to
// unlock more themes (textured papers, seasonal sets, custom colors)
// later without changing the wire format — only the theme id travels in
// the envelope, and unknown ids fall back to the default.

export type LetterTheme = {
  id: string;
  name: string;
  paper: string; // page background
  ink: string; // text color
  line: string; // ruled guide line color (rgba)
};

// Pulled apart so they read as clearly different papers, not six shades of
// pale. Each pairs a tinted paper with a matching darker ink.
export const LETTER_THEMES: LetterTheme[] = [
  { id: 'cream', name: 'Cream', paper: '#f3ecdd', ink: '#3a3326', line: 'rgba(120,98,60,.22)' },
  { id: 'rose', name: 'Rose', paper: '#f6dfe0', ink: '#5a2f37', line: 'rgba(176,90,104,.26)' },
  { id: 'sage', name: 'Sage', paper: '#dde9dd', ink: '#26392e', line: 'rgba(70,120,86,.26)' },
  { id: 'sky', name: 'Sky', paper: '#d9e6f2', ink: '#243749', line: 'rgba(70,108,150,.26)' },
  { id: 'lilac', name: 'Lilac', paper: '#e7ddf3', ink: '#39295a', line: 'rgba(124,98,170,.26)' },
  { id: 'butter', name: 'Butter', paper: '#f6edc4', ink: '#4a4012', line: 'rgba(170,150,40,.28)' },
];

export const DEFAULT_LETTER_THEME = LETTER_THEMES[0];

export function getLetterTheme(id: string | undefined): LetterTheme {
  if (!id) return DEFAULT_LETTER_THEME;
  return LETTER_THEMES.find((t) => t.id === id) ?? DEFAULT_LETTER_THEME;
}

/** CSS custom properties to apply a theme onto a letter surface. */
export function themeVars(theme: LetterTheme): React.CSSProperties {
  return {
    ['--paper' as string]: theme.paper,
    ['--paper-ink' as string]: theme.ink,
    ['--paper-line' as string]: theme.line,
  };
}
